import { useCallback, useMemo, useState } from 'react';
import { PiPlusBold, PiUploadSimpleBold } from 'react-icons/pi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import ShipmentListFilters from '@v2/features/shipments/ShipmentListFilters';
import ShipmentListTable from '@v2/features/shipments/ShipmentListTable';
import ShipmentStats from '@v2/features/shipments/ShipmentStats';
import type {
    PaginatedShipments,
    ShipmentListFilters as Filters,
    ShipmentListRow,
    ShipmentStats as Stats,
} from '@v2/features/shipments/types';
import { exportShipmentsCsv } from '@v2/features/shipments/utils';

type StageSetting = { stage: string; row_color: string | null };

function useStageColorMap() {
    const { data } = useQuery<StageSetting[]>({
        queryKey: ['stage-settings'],
        queryFn: async () => {
            const { data } = await api.get<{ data: StageSetting[] }>('/stage-settings');
            return data.data;
        },
        staleTime: 60_000,
    });
    return useMemo(() => {
        const map: Record<string, string | null> = {};
        for (const s of data ?? []) map[s.stage] = s.row_color;
        return map;
    }, [data]);
}

const defaultFilters: Filters = {
    search: '',
    process_status: '',
    stage: '',
    delay_risk_level: '',
    order_date_from: '',
    order_date_to: '',
    page: 1,
    per_page: 25,
    sort: '-id',
};

function buildParams(filters: Filters): Record<string, string | number> {
    const params: Record<string, string | number> = {
        page: filters.page,
        per_page: filters.per_page,
        sort: filters.sort,
    };
    if (filters.search) params.search = filters.search;
    if (filters.process_status) params.process_status = filters.process_status;
    if (filters.stage) params.stage = filters.stage;
    if (filters.delay_risk_level) params.delay_risk_level = filters.delay_risk_level;
    if (filters.order_date_from) params.order_date_from = filters.order_date_from;
    if (filters.order_date_to) params.order_date_to = filters.order_date_to;

    return params;
}

export default function ShipmentListPage() {
    const [filters, setFilters] = useState<Filters>(defaultFilters);
    const [showExtraFilters, setShowExtraFilters] = useState(true);
    const queryParams = useMemo(() => buildParams(filters), [filters]);
    const stageColorMap = useStageColorMap();
    const qc = useQueryClient();

    const { data: stats, isLoading: statsLoading } = useQuery({
        queryKey: ['shipments', 'stats'],
        queryFn: async () => {
            const { data } = await api.get<Stats>('/shipments/stats');
            return data;
        },
    });

    const { data: listResponse, isLoading: listLoading, isFetching } = useQuery({
        queryKey: ['shipments', 'list', queryParams],
        queryFn: async () => {
            const { data } = await api.get<PaginatedShipments>('/shipments', { params: queryParams });
            return data;
        },
        placeholderData: (prev) => prev,
    });

    // Mescla as cores do cliente com os dados da lista (sempre atualizado, sem depender de refetch)
    const rows: ShipmentListRow[] = useMemo(
        () => (listResponse?.data ?? []).map((row) => ({
            ...row,
            stage_row_color: row.stage ? (stageColorMap[row.stage] ?? null) : null,
        })),
        [listResponse?.data, stageColorMap],
    );
    const meta = listResponse?.meta;
    const lastPage = meta?.last_page ?? 1;
    const total = meta?.total ?? 0;

    const patchFilters = useCallback((patch: Partial<Filters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilters((prev) => ({ ...defaultFilters, per_page: prev.per_page }));
    }, []);

    const handleExport = () => {
        exportShipmentsCsv(rows, `embarques_${new Date().toISOString().slice(0, 10)}.csv`);
    };

    return (
        <div className="flex flex-col gap-10">
            <PageHeader
                title="Embarques"
                breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Lista de embarques' }]}
            >
                <Button variant="outline" onClick={handleExport} disabled={rows.length === 0} className="w-full @lg:w-auto">
                    <PiUploadSimpleBold className="me-1.5 h-[17px] w-[17px]" />
                    Exportar
                </Button>
                <a href="/shipments/new" className="w-full @lg:w-auto">
                    <Button as="span" className="w-full @lg:w-auto">
                        <PiPlusBold className="me-1.5 h-[17px] w-[17px]" />
                        Novo embarque
                    </Button>
                </a>
            </PageHeader>

            <ShipmentStats stats={stats} loading={statsLoading} />

            <section>
                <ShipmentListFilters
                    filters={filters}
                    showExtra={showExtraFilters}
                    onChange={patchFilters}
                    onToggleExtra={() => setShowExtraFilters((v) => !v)}
                    onClear={clearFilters}
                />

                {isFetching && !listLoading && <p className="mb-2 text-xs text-gray-400">Atualizando lista…</p>}

                <ShipmentListTable
                    rows={rows}
                    loading={listLoading}
                    page={filters.page}
                    perPage={filters.per_page}
                    lastPage={lastPage}
                    total={total}
                    onPageChange={(page) => patchFilters({ page })}
                    onPerPageChange={(per_page) => patchFilters({ per_page, page: 1 })}
                    onDeleted={() => qc.invalidateQueries({ queryKey: ['shipments'] })}
                />
            </section>
        </div>
    );
}
